import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import get from 'lodash/get';
import { useSelector } from 'react-redux';
import Button from 'carbon-react/lib/components/button';

import { UISystemIcon } from '@futrli/ui-components';
import {
  getIntegrationsOnboardingUrl,
  getXeroAdditionalUrl,
  useSessionStorage,
  PREDICT_PRODUCT_ID
} from '@futrli/shared-services';
import { AuthStoreState } from '@futrli/auth';

import * as styles from './onboarding.scss';
import { OnboardingNavbar } from './onboarding-navbar';
import { FEDERATED_SIGN_IN_STORE_KEY } from '../../../../auth/src/federated-sign-in/constants';
import { FederatedSignInStorage } from '../../../../auth/src/federated-sign-in/types';

interface Props {
  readonly workspaceId: string;
}

export const OnboardingIntro = ({ workspaceId }: Props) => {
  const navigate = useNavigate();

  const [storage] = useSessionStorage<FederatedSignInStorage | null>(FEDERATED_SIGN_IN_STORE_KEY, null);

  const firstName = useSelector((state: AuthStoreState) =>
    get(state, 'authReducers.authenticatedUser.attributes.given_name', '')
  );

  const handleNextClick = useCallback(() => {
    if (!workspaceId) {
      return;
    }

    // There is additional provider in storage which means user needs to be redirected to additional info page
    if (storage?.federatedIdentityProvider === 'Xero') {
      const savedQueryParams = storage?.originLocationSearch;
      navigate(
        `${getXeroAdditionalUrl()}${
          savedQueryParams ? `${savedQueryParams}&workspace_id=${workspaceId}` : `?workspace_id=${workspaceId}`
        }`
      );
      return;
    }

    navigate(getIntegrationsOnboardingUrl(workspaceId, '', PREDICT_PRODUCT_ID));
  }, [navigate, storage?.federatedIdentityProvider, storage?.originLocationSearch, workspaceId]);

  return (
    <>
      <OnboardingNavbar />
      <div className={styles.container}>
        <div style={{ height: 200 }} />
        <h1 className={styles.title} style={{ marginBottom: 10 }}>
          <FormattedMessage
            id="onboarding.welcome"
            defaultMessage="Welcome! We’re glad you’re here {firstName}."
            values={{ firstName }}
          />
        </h1>
        <div className={styles.subTitle} style={{ marginBottom: 70 }}>
          <FormattedMessage id="onboarding.do_together" defaultMessage="This is what we’ll do together now." />
        </div>
        <div className={styles.introImgContainer}>
          <div className={styles.imgDescContainer}>
            <UISystemIcon iconName="onboarding-intro-1" width={150} height={150} />
            <div className={styles.description} style={{ marginTop: 40, maxWidth: 150 }}>
              <FormattedMessage id="onboarding.data_connected" defaultMessage="We’ll get your data connected" />
            </div>
          </div>
          <UISystemIcon iconName="onboarding-intro-arrow" width={68} height={6} />
          <div className={styles.imgDescContainer}>
            <UISystemIcon iconName="onboarding-intro-2" width={150} height={150} />
            <div className={styles.description} style={{ marginTop: 40, maxWidth: 220 }}>
              <FormattedMessage
                id="onboarding.tell_us_futrli"
                defaultMessage="You’ll tell us what you’re here to do with Futrli"
              />
            </div>
          </div>
          <UISystemIcon iconName="onboarding-intro-arrow" width={68} height={6} />
          <div className={styles.imgDescContainer}>
            <UISystemIcon iconName="onboarding-intro-3" width={150} height={150} />
            <div className={styles.description} style={{ marginTop: 40, maxWidth: 220 }}>
              <FormattedMessage
                id="onboarding.get_you_started"
                defaultMessage="We’ll get you started on what you’re here for"
              />
            </div>
          </div>
        </div>
        <Button mt="70px" buttonType="primary" size="large" onClick={handleNextClick}>
          <FormattedMessage id="common.next" defaultMessage="Next" />
        </Button>
      </div>
    </>
  );
};
